/**
 * Kom i gang (#288 slice A, #294; reference docs/design/prototypes/onboarding-handoff-2026-11-02,
 * screen 02): the work cycle Opprett → Kontroller → Rapporter og lønn as the company's own steps,
 * each decided by a saved fact on the server (GET /onboarding), never by a page visited or a tip
 * closed (ONB-03). One next action on top, the phases with «x av y» under it, a row per step that
 * opens the page where the work is done. Pausing the guide is this person's preference and changes
 * no step. The first working day is chosen here (ONB-02); Kontroll waits for it (ONB-12).
 * #297: a step that is not the reader's to do says who does it instead of offering its page (ONB-25);
 * tips on e-mail are the reader's own choice, with «Minn meg senere» per step (ONB-25/26); a started
 * optional setup is resumed from here (ONB-23/24); an old link (?oppgave=…&firma=…, from a tip) opens
 * the current state and does nothing by itself: another company is named and switched to only by a
 * press, a finished step says so (ONB-26).
 */
import { errorText } from '@/api/client';
import { useTenantWrite, useWorkspaceWrite } from '@/api/company';
import {
  type EducationWait,
  type Onboarding,
  type Phase,
  type SetupDraft,
  type Step,
  type StepKey,
  useEducation,
  useGuidePause,
  useOnboarding,
  useSetupDrafts,
} from '@/api/onboarding';
import { T } from '@/i18n';
import { useCompanySwitch } from '@/shell/switch';
import { useTenant } from '@/shell/tenant';
import { Button, LoadError, Skeleton, toast } from '@/ui';
import { DateInput } from '@/ui/DateInput';
import { dayMonth, fmtD } from '@/ui/format';
import { Icon, type IconName } from '@/ui/icons';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { missingText } from './innstillinger/ruleProfile';
import s from './komigang.module.css';

const PHASES: { key: Phase; title: string; icon: IconName }[] = [
  { key: 'opprett', title: 'Opprett', icon: 'calendar' },
  { key: 'kontroller', title: 'Kontroller', icon: 'check' },
  { key: 'rapporter', title: 'Rapporter og lønn', icon: 'document' },
];

interface Copy {
  title: string;
  icon: IconName;
  /** where the work is done; absent for the start date, which is chosen on this page */
  to?: string | ((o: Onboarding) => string);
  /** a second way to finish the step, under the main action */
  alt?: { to: string; label: string };
  open: (o: Onboarding, n: number) => string;
  done: (o: Onboarding) => string;
  /** a step that counts people: the line is the figure «x av y», coloured by state (owner rule 2026-10-25) */
  counts?: (o: Onboarding) => boolean;
}
/** «Ingen ansatte ennå»: a step that counts people has nothing to count yet, which is not a 0 (#288 review) */
const nobody = () => T('Ingen ansatte ennå. Legg dem til først.');
/** what each step says, open and done, in the manager's words */
const COPY: Record<StepKey, Copy> = {
  arbeidsmate: {
    title: 'Velg arbeidsmåte og startdato',
    icon: 'calendar',
    open: () =>
      T(
        'Planlegger dere vakter, eller fører dere bare timer? Og når starter dere? Kontroll venter til denne dagen.',
      ),
    done: (o) =>
      T('{mode} · Første arbeidsdag {date}', {
        mode: o.mode === 'rota' ? T('Vaktplan og timer') : T('Bare timer'),
        date: o.start_date ? dayMonth(o.start_date) : '',
      }) +
      // ONB-10: switching to time-only deletes no plan draft; it waits for the plan to be turned on again
      (o.mode === 'time' && o.facts.drafts
        ? ` · ${o.facts.drafts === 1 ? T('1 planutkast er tatt vare på') : T('{n} planutkast er tatt vare på', { n: o.facts.drafts })}`
        : ''),
  },
  ansatte: {
    title: 'Legg til ansatte',
    icon: 'userPlus',
    to: '/ansatte/importer',
    open: () => T('Hent dem fra lønnssystemet eller legg dem inn selv.'),
    // a plain count: there is no «of how many» for people added, a company has no target headcount
    done: (o) =>
      o.facts.people === 1 ? T('1 ansatt lagt til.') : T('{n} ansatte lagt til.', { n: o.facts.people }),
  },
  arbeidstid: {
    title: 'Klargjør ansatte',
    icon: 'users',
    to: '/ansatte?oppgave=arbeidstid',
    open: (o, n) => (o.facts.people ? weekLine(o, members(o) - n) : nobody()),
    done: (o) => weekLine(o, members(o)),
    counts: (o) => o.facts.people > 0,
  },
  // #294 ONB-08: the server's profile status, the one Innstillinger shows
  regler: {
    title: 'Gå gjennom bransje og tariffavtale',
    icon: 'sliders',
    to: '/innstillinger/regelprofil',
    open: (o) =>
      o.rule_profile?.missing.length
        ? T('Mangler: {what}.', { what: missingText(o.rule_profile.missing) })
        : T('Alt er fylt ut. Bekreft gjennomgangen.'),
    done: (o) =>
      o.rule_profile?.agreement === 'yes' && o.rule_profile.agreement_name
        ? T('Gjennomgått · {name}', { name: o.rule_profile.agreement_name })
        : T('Gjennomgått · ingen tariffavtale'),
  },
  vaktplan: {
    title: 'Lag og publiser første vaktplan',
    icon: 'calendar',
    to: '/vaktplan',
    open: (o) =>
      o.facts.drafts
        ? o.facts.drafts === 1
          ? T('1 vakt i utkast. Se hvem som får den, og publiser. Ingen ser utkast.')
          : T('{n} vakter i utkast. Se hvem som får dem, og publiser. Ingen ser utkast.', {
              n: o.facts.drafts,
            })
        : T('Lag et utkast, se over og publiser. De ansatte ser planen først når den er publisert.'),
    // «x av y» (owner rule 2026-10-25): of the plan's shifts, published and still in draft; the noun follows the total
    done: (o) => {
      const b = o.facts.published + (o.facts.drafts ?? 0);
      return T(b === 1 ? '{a} av {b} vakt publisert.' : '{a} av {b} vakter publisert.', {
        a: o.facts.published,
        b,
      });
    },
    counts: () => true,
  },
  // done when everyone has got in to THIS company; an invitation, or a login elsewhere, is not access (PROM-28).
  // Nobody left to invite: the list of those still waiting for their first session here
  tilgang: {
    title: 'Gi de ansatte tilgang',
    icon: 'login',
    to: (o) => (o.facts.uninvited > 0 ? '/ansatte?oppgave=ikke-invitert' : '/ansatte?oppgave=venter'),
    open: (o) =>
      o.facts.people
        ? T('{a} av {b} er invitert. {n} av {b} har logget inn.', {
            a: o.facts.people - o.facts.uninvited,
            b: o.facts.people,
            n: o.facts.logged_in,
          })
        : nobody(),
    done: (o) =>
      T('{a} av {b} er invitert. {n} av {b} har logget inn.', {
        a: o.facts.people,
        b: o.facts.people,
        n: o.facts.logged_in,
      }),
    counts: (o) => o.facts.people > 0,
  },
  registrert: {
    title: 'Registrer første arbeidsdag',
    icon: 'clock',
    // time-only has no plan to wait for: the step opens Legg til registrering in Kontroll (ONB-10)
    to: (o) => (o.mode === 'time' ? '/timeliste?ny=registrering' : '/timeliste'),
    open: (o) =>
      o.mode === 'time'
        ? T('De ansatte stempler i Timefly, eller du legger inn start, slutt og pause i Kontroll.')
        : T('Ansatte stempler i Timefly, eller du registrerer start, slutt og pause.'),
    done: (o) =>
      o.facts.worked === 1
        ? T('1 registrering med stempling.')
        : T('{n} registreringer med stempling.', { n: o.facts.worked }),
  },
  godkjent: {
    title: 'Kontroller og godkjenn timer',
    icon: 'check',
    to: '/timeliste?vis=klare',
    open: () => T('Rett mangler, se beregningen og godkjenn. Å rette godkjenner ikke.'),
    // of the records with a clock-in; a company that keeps hours only has records, not shifts (#288 review)
    // the noun follows the total: «1 av 1 registrering», «1 av 2 registreringer»
    done: (o) => {
      const b = Math.max(o.facts.worked, o.facts.approved);
      const time = o.mode === 'time';
      const text =
        b === 1
          ? time
            ? '{a} av {b} registrering godkjent.'
            : '{a} av {b} vakt godkjent.'
          : time
            ? '{a} av {b} registreringer godkjent.'
            : '{a} av {b} vakter godkjent.';
      return T(text, { a: o.facts.approved, b });
    },
    counts: () => true,
  },
  // «Hopp over oppsettet» can leave it unchosen (owner 2026-11-04); the choice is made in Innstillinger, Lønn
  lonnssystem: {
    title: 'Velg lønnssystem',
    icon: 'sliders',
    to: '/innstillinger/lonn#lonnssystem',
    open: () =>
      T(
        'Velg systemet dere kjører lønn i, eller «Annet, eller vet ikke». Lønnsartnumrene i Timefly følger det.',
      ),
    done: () => T('Lønnssystemet er valgt.'),
  },
  rapport: {
    title: 'Lag første rapport',
    icon: 'download',
    to: '/lonn',
    // report-only (#295 ONB-16): approved hours from Kontroll, no payroll system or pay codes needed
    alt: { to: '/timeliste?rapport=godkjente', label: 'Bare rapport over godkjente timer' },
    open: () =>
      T('Lag lønnsgrunnlag til lønnssystemet, eller bare en rapport over godkjente timer uten lønnsoppsett.'),
    done: (o) => (o.facts.reports === 1 ? T('1 fil laget.') : T('{n} filer laget.', { n: o.facts.reports })),
  },
};

/**
 * What an open step waits for (#154 OP-03): the step whose saved fact it needs first, named in a neutral
 * label rather than shown as an alarm. A step whose prerequisite is done says nothing extra.
 */
// arbeidstid and tilgang already say «Ingen ansatte ennå. Legg dem til først.» in their own line
const NEEDS: Partial<Record<StepKey, StepKey>> = {
  vaktplan: 'ansatte',
  registrert: 'arbeidsmate',
  godkjent: 'registrert',
  rapport: 'godkjent',
};
const waitsFor = (o: Onboarding, step: Step) => {
  const need = NEEDS[step.key];
  if (step.done || !need) return null;
  const pre = o.steps.find((x) => x.key === need);
  return pre && !pre.done ? pre : null;
};

export default function KomIGang() {
  const q = useOnboarding();
  const pause = useGuidePause();
  if (q.isPending) return <Skeleton rows={6} />;
  if (q.isError || !q.data)
    return <LoadError what={T('Kom i gang')} detail={errorText(q.error)} onRetry={() => q.refetch()} />;
  const o = q.data;
  const next = o.steps.find((x) => !x.done);
  const paused = !!o.paused_at;
  return (
    <div className={s.page} data-testid="komigang">
      <header className={s.head}>
        <span className={s.mark} aria-hidden="true">
          <Icon name="flag" size={22} />
        </span>
        <div>
          <h1>{T('Kom i gang')}</h1>
          <p>{T('Fra de første ansatte til kontrollert lønnsgrunnlag.')}</p>
        </div>
        <button
          type="button"
          className={s.textBtn}
          data-testid="guide-pause"
          disabled={pause.isPending}
          onClick={() =>
            pause.mutate(!paused, {
              onSuccess: () =>
                toast(paused ? T('Veiledningen er på igjen.') : T('Veiledningen er satt på pause.')),
              onError: (e) => toast(errorText(e)),
            })
          }
        >
          {paused ? T('Fortsett veiledningen') : T('Pause veiledningen')}
        </button>
      </header>

      {paused && (
        <p className={s.note} data-testid="guide-paused">
          {T('Veiledningen er på pause. Stegene under følger fortsatt det dere har lagret.')}
        </p>
      )}

      <LinkNotice o={o} />
      <SetupEntry o={o} />

      {next ? <Next o={o} step={next} /> : <AllDone />}

      <ol className={s.phases} aria-label={T('Faser')}>
        {PHASES.map((p) => {
          const list = o.steps.filter((x) => x.phase === p.key);
          const done = list.filter((x) => x.done).length;
          return (
            <li key={p.key} data-testid={`phase-${p.key}`}>
              <span className={`${s.pIcon} ${done === list.length ? s.pDone : ''}`} aria-hidden="true">
                <Icon name={p.icon} size={18} />
              </span>
              <span>
                <b>{T(p.title)}</b>
                <span className={s.meta}>{T('{done} av {all} ferdige', { done, all: list.length })}</span>
                <span className={s.bar} aria-hidden="true">
                  {list.map((x) => (
                    <i key={x.key} className={x.done ? s.on : undefined} />
                  ))}
                </span>
              </span>
            </li>
          );
        })}
      </ol>

      {PHASES.map((p) => (
        <section key={p.key} className={s.section} aria-labelledby={`kig-${p.key}`}>
          <h2 id={`kig-${p.key}`}>{T(p.title)}</h2>
          <ul>
            {o.steps
              .filter((x) => x.phase === p.key)
              .map((x) => (
                <Row key={x.key} o={o} step={x} />
              ))}
          </ul>
        </section>
      ))}

      <OptionalSetup />
      <Tips o={o} />
    </div>
  );
}

// the agreed-week step counts the caller too (Ansatte's rule), so its total does as well; people leaves the caller out
const members = (o: Onboarding) => o.facts.members ?? o.facts.people + 1;
/**
 * «x av y har avtalt arbeidstid»: y counts the caller, «ansatte lagt til» and «er invitert» do not, so the
 * line says so whenever the two totals differ («9 ansatte lagt til» beside «10 av 10», regression review 2026-11-04)
 */
const weekLine = (o: Onboarding, a: number) =>
  members(o) > o.facts.people
    ? T('{a} av {b} har avtalt arbeidstid, inkludert deg.', { a, b: members(o) })
    : T('{a} av {b} har avtalt arbeidstid.', { a, b: members(o) });
const hrefOf = (c: Copy, o: Onboarding) => (typeof c.to === 'function' ? c.to(o) : c.to);

function lineOf(o: Onboarding, step: Step) {
  const c = COPY[step.key];
  return step.done ? c.done(o) : c.open(o, step.count ?? 0);
}
/** the step's line; a figure «x av y» takes the state's colour, done or still open */
function Line({ o, step }: { o: Onboarding; step: Step }) {
  const counted = !!COPY[step.key].counts?.(o) && (step.done || step.count !== undefined);
  return (
    <span
      className={counted ? (step.done ? s.figOk : s.figOpen) : undefined}
      data-figure={counted || undefined}
    >
      {lineOf(o, step)}
    </span>
  );
}

/** The first open step, as the one thing to do now. */
function Next({ o, step }: { o: Onboarding; step: Step }) {
  const c = COPY[step.key];
  return (
    <div className={s.next} data-testid="kig-next">
      <span className={s.nIcon} aria-hidden="true">
        <Icon name={c.icon} size={22} />
      </span>
      <div>
        <h2>{T(c.title)}</h2>
        <p>
          <Line o={o} step={step} />
        </p>
        {step.mine === false ? (
          // ONB-25: no page to press for a step the reader may not do; it waits for the one who may
          <p className={s.meta} data-testid="kig-next-other">
            {T('Dette steget gjøres av en med tilgang til det. Stegene under oppdateres når det er lagret.')}
          </p>
        ) : step.key === 'arbeidsmate' ? (
          <StartDate o={o} />
        ) : (
          <Link className="btn pri" to={hrefOf(c, o) ?? '/'} data-testid="kig-next-go">
            {T(c.title)}
            <Icon name="arrowRight" size={16} />
          </Link>
        )}
        {c.alt && step.mine !== false && (
          <Link className={`mlink ${s.alt}`} to={c.alt.to} data-testid="kig-next-alt">
            {T(c.alt.label)}
          </Link>
        )}
        {step.mine !== false && o.education?.opt_in && <Defer o={o} step={step} />}
      </div>
    </div>
  );
}

function AllDone() {
  return (
    <div className={`${s.next} ${s.done}`} data-testid="kig-done">
      <span className={`${s.nIcon} ${s.pDone}`} aria-hidden="true">
        <Icon name="check" size={22} />
      </span>
      <div>
        <h2>{T('Alt er på plass')}</h2>
        <p>
          {T('Dere har gått hele veien fra ansatte til rapport. Siden ligger her om dere vil se den igjen.')}
        </p>
      </div>
    </div>
  );
}

function Row({ o, step }: { o: Onboarding; step: Step }) {
  const c = COPY[step.key];
  const [params] = useSearchParams();
  const asked = params.get('oppgave') === step.key;
  // the saved way of working and start date stay changeable from the done row (#288 review fix 2): the same
  // form and the same revision check as the first choice; the open step is chosen in Next above
  const { tenant } = useTenant();
  const [editing, setEditing] = useState(false);
  const editable = step.key === 'arbeidsmate' && step.done && tenant?.me?.is_admin === true;
  return (
    <li
      className={`${s.row} ${asked ? s.asked : ''}`}
      id={`kig-step-${step.key}`}
      data-testid={`step-${step.key}`}
      data-done={step.done}
    >
      <span className={`${s.dot} ${step.done ? s.dotDone : ''}`} aria-hidden="true">
        {step.done && <Icon name="check" size={14} strokeWidth={2.4} />}
      </span>
      <span className={s.rText}>
        <b>{T(c.title)}</b>
        <Line o={o} step={step} />
        {(() => {
          const pre = waitsFor(o, step);
          return pre ? (
            <span className={s.dep} data-testid={`step-${step.key}-waits`}>
              <Icon name="clock" size={14} />
              {T('{step} først', { step: T(COPY[pre.key].title) })}
            </span>
          ) : null;
        })()}
        <span className="sr-only">{step.done ? T('Ferdig') : T('Ikke ferdig')}</span>
      </span>
      {editable ? (
        <button
          type="button"
          className={`${s.go} ${s.goBtn}`}
          aria-expanded={editing}
          aria-label={`${editing ? T('Lukk') : T('Endre')}: ${T(c.title)}`}
          aria-controls="kig-edit-arbeidsmate"
          onClick={() => setEditing((v) => !v)}
          data-testid="kig-edit-start"
        >
          {editing ? T('Lukk') : T('Endre')}
          <Icon name={editing ? 'close' : 'chevronDown'} size={16} />
        </button>
      ) : c.to && (step.done || step.mine !== false) ? (
        <Link
          className={s.go}
          to={hrefOf(c, o) as string}
          aria-label={`${step.done ? T('Se') : T('Gå til')}: ${T(c.title)}`}
        >
          {step.done ? T('Se') : T('Gå til')}
          <Icon name="chevronRight" size={16} />
        </Link>
      ) : !step.done && step.mine === false ? (
        <span className={s.other}>{T('Gjøres av en annen')}</span>
      ) : null}
      {editable && editing && (
        <div className={s.rEdit} id="kig-edit-arbeidsmate">
          <StartDate o={o} onSaved={() => setEditing(false)} />
        </div>
      )}
    </li>
  );
}

/**
 * How the company works and its first working day (ONB-02, ONB-10): saved to the workspace in one
 * write with the revision last read (a stale one is refused). Time-only keeps every plan draft; the
 * plan step leaves the list and Kontroll is where work is recorded.
 */
function StartDate({ o, onSaved }: { o: Onboarding; onSaved?: () => void }) {
  const { tenant } = useTenant();
  const write = useWorkspaceWrite();
  const [date, setDate] = useState(o.start_date ?? '');
  const [mode, setMode] = useState<Onboarding['mode']>(o.mode);
  if (!tenant?.me?.is_admin)
    return <p className={s.meta}>{T('Administratoren velger arbeidsmåte og startdato.')}</p>;
  return (
    <form
      className={s.date}
      onSubmit={(e) => {
        e.preventDefault();
        if (!date) return;
        write.mutate(
          {
            start_date: date,
            ...(mode !== o.mode ? { scheduling_enabled: mode === 'rota' } : {}),
            revision: o.revision,
          },
          {
            onSuccess: () => {
              toast(T('Arbeidsmåte og startdato er lagret.'));
              onSaved?.();
            },
            onError: (err) => toast(errorText(err)),
          },
        );
      }}
    >
      <fieldset className={s.mode}>
        <legend>{T('Arbeidsmåte')}</legend>
        {(
          [
            ['rota', T('Vaktplan og timer'), T('Dere lager vaktplan, og timene kontrolleres mot den.')],
            [
              'time',
              T('Bare timer'),
              T('Ingen vaktplan. De ansatte stempler, eller du legger inn arbeidet.'),
            ],
          ] as const
        ).map(([v, label, line]) => (
          <label key={v} className={s.modeOpt}>
            <input
              type="radio"
              name="kig-mode"
              value={v}
              checked={mode === v}
              onChange={() => setMode(v)}
              data-testid={`kig-mode-${v}`}
            />
            <span>
              <b>{label}</b>
              <span>{line}</span>
            </span>
          </label>
        ))}
        {mode === 'time' && o.mode === 'rota' && !!o.facts.drafts && (
          <p className={s.meta} data-testid="kig-mode-drafts">
            {T('{n} vaktutkast blir liggende og kommer tilbake om dere slår på vaktplan igjen.', {
              n: o.facts.drafts,
            })}
          </p>
        )}
      </fieldset>
      <div className={s.dateRow}>
        <label htmlFor="kig-start">
          <span>{T('Første arbeidsdag')}</span>
        </label>
        <DateInput
          className="inp"
          id="kig-start"
          value={date}
          onValueChange={setDate}
          data-testid="kig-start"
        />
        <Button tone="pri" type="submit" disabled={!date || write.isPending} data-testid="kig-start-save">
          {T('Lagre')}
        </Button>
      </div>
    </form>
  );
}

const STEP_KEYS = Object.keys(COPY) as StepKey[];
const isStep = (k: string | null): k is StepKey => !!k && (STEP_KEYS as string[]).includes(k);

/**
 * An old link (from a tip on e-mail, or a bookmark) re-read against now (#297 ONB-26, G15): the company
 * it names, the step it names and whether that step is done or the reader's. Nothing is done by the link:
 * another company is switched to only by the button, a finished step is never replayed.
 */
function LinkNotice({ o }: { o: Onboarding }) {
  const [params, setParams] = useSearchParams();
  const { tenant, tenants } = useTenant();
  const { switchTo } = useCompanySwitch();
  const ref = useRef<HTMLDivElement>(null);
  const task = params.get('oppgave');
  const firma = Number(params.get('firma'));
  const other = Number.isInteger(firma) && firma > 0 && tenant != null && firma !== tenant.id;
  const target = other ? tenants.find((t) => t.id === firma) : undefined;
  const step = isStep(task) ? o.steps.find((x) => x.key === task) : undefined;
  // the notice is where the link landed: a reader on a screen reader hears it first (G15, ONB-28)
  useEffect(() => {
    if (task) ref.current?.focus();
  }, [task]);
  if (!task) return null;
  const close = () => {
    params.delete('oppgave');
    params.delete('firma');
    setParams(params, { replace: true });
  };
  let text: string;
  let action: React.ReactNode = null;
  if (other && target) {
    text = T('Lenken gjelder {company}. Du ser nå {current}. Ingenting er endret i noen av dem.', {
      company: target.name,
      current: tenant?.name ?? '',
    });
    action = (
      <Button small onClick={() => switchTo(target.id)} data-testid="kig-link-switch">
        {T('Bytt til {company}', { company: target.name })}
      </Button>
    );
  } else if (other) {
    text = T('Lenken gjelder en bedrift du ikke lenger har tilgang til. Her er stegene for {current}.', {
      current: tenant?.name ?? '',
    });
  } else if (task === 'lonnskobling') {
    text = T('Lenken gjelder utkastet ditt til tilkoblingen. Se hvordan det står under Valgfritt oppsett.');
  } else if (task === 'import') {
    // the tip for an import put off (#154 HUG-09): the way back is the setup entry above the steps
    text = T('Lenken gjelder importen du satte på vent. Fortsett den med «Fortsett importen».');
  } else if (!step) {
    text = T('Lenken gjelder et steg som ikke finnes for bedriften lenger. Her er stegene slik de står nå.');
  } else if (step.done) {
    text = T('«{title}» er allerede gjort. Det står igjen å gjøre det som er åpent under.', {
      title: T(COPY[step.key].title),
    });
  } else if (step.mine === false) {
    text = T('«{title}» gjøres av en med tilgang til det. Du trenger ikke gjøre noe.', {
      title: T(COPY[step.key].title),
    });
  } else {
    text = T('«{title}» står fortsatt åpent. Det er markert i listen under.', {
      title: T(COPY[step.key].title),
    });
  }
  return (
    <div className={s.link} ref={ref} tabIndex={-1} aria-live="polite" data-testid="kig-link">
      <Icon name="info" size={18} />
      <p>{text}</p>
      <span className={s.linkActs}>
        {action}
        <button type="button" className={s.textBtn} onClick={close}>
          {T('Lukk')}
        </button>
      </span>
    </div>
  );
}

/** «Minn meg senere»: no tip about this step before the date chosen; the step itself is unchanged (ONB-26). */
function Defer({ o, step }: { o: Onboarding; step: Step }) {
  const edu = useEducation();
  const until = o.education?.deferred[step.key];
  const [date, setDate] = useState(until ?? '');
  const [open, setOpen] = useState(false);
  if (until && !open)
    return (
      <p className={s.meta} data-testid="kig-deferred">
        {T('Ingen tips om dette steget før {date}.', { date: fmtD(until) })}{' '}
        <button
          type="button"
          className={s.textBtn}
          disabled={edu.isPending}
          onClick={() =>
            edu.mutate({ defer: { step: step.key, until: null } }, { onError: (e) => toast(errorText(e)) })
          }
        >
          {T('Fjern')}
        </button>
      </p>
    );
  if (!open)
    return (
      <button type="button" className={s.textBtn} onClick={() => setOpen(true)} data-testid="kig-defer">
        {T('Minn meg senere')}
      </button>
    );
  return (
    <form
      className={s.date}
      onSubmit={(e) => {
        e.preventDefault();
        if (!date) return;
        edu.mutate(
          { defer: { step: step.key, until: date } },
          {
            onSuccess: () => {
              setOpen(false);
              toast(T('Du får ingen tips om dette steget før {date}.', { date: fmtD(date) }));
            },
            onError: (err) => toast(errorText(err)),
          },
        );
      }}
    >
      <label htmlFor="kig-defer-date">
        <span>{T('Minn meg på dette fra')}</span>
      </label>
      <DateInput
        className="inp"
        id="kig-defer-date"
        value={date}
        onValueChange={setDate}
        data-testid="kig-defer-date"
      />
      <Button type="submit" disabled={!date || edu.isPending} data-testid="kig-defer-save">
        {T('Lagre')}
      </Button>
      <button type="button" className={s.textBtn} onClick={() => setOpen(false)}>
        {T('Avbryt')}
      </button>
    </form>
  );
}

const WAIT: Partial<Record<EducationWait, string>> = {
  demo: 'Demobedrifter får ingen tips på e-post.',
  not_yours: 'Det neste steget gjøres av en annen med tilgang. Du får tips når det er et steg du kan gjøre.',
  waiting_for_start: 'Ingen tips om timer før den første arbeidsdagen.',
  deferred: 'Du har bedt om å bli minnet på det neste steget senere.',
  nothing_open: 'Ingen steg står igjen, så det kommer ingen tips.',
  cap_day: 'Du har fått et tips nylig. Det kommer høyst ett om dagen og tre i uken.',
  cap_week: 'Du har fått et tips nylig. Det kommer høyst ett om dagen og tre i uken.',
  cap_company: 'Du har fått alle tipsene for denne bedriften.',
  cap_task: 'Du har fått to tips om dette steget. Det kommer ikke flere om det.',
  task_gap: 'Du har fått et tips om dette steget den siste uken.',
  unknown: 'Stegene kunne ikke leses, så det sendes ingen tips nå.',
};

/**
 * Tips on e-mail (#297 ONB-25, ADM02): off until the reader turns them on, theirs in this company. Says
 * what would happen now in the server's own words, and that nothing is sent where sending is off.
 */
function Tips({ o }: { o: Onboarding }) {
  const edu = useEducation();
  const e = o.education;
  if (!e) return null;
  const why = e.opt_in ? (e.wait ? WAIT[e.wait] : undefined) : undefined;
  return (
    <section className={s.section} aria-labelledby="kig-tips">
      <h2 id="kig-tips">{T('Tips på e-post')}</h2>
      <div className={s.tips}>
        <label className={s.check}>
          <input
            type="checkbox"
            checked={e.opt_in}
            disabled={edu.isPending}
            onChange={(ev) =>
              edu.mutate(
                { opt_in: ev.target.checked },
                {
                  onSuccess: (r) => toast(r.opt_in ? T('Tips er slått på.') : T('Tips er slått av.')),
                  onError: (err) => toast(errorText(err)),
                },
              )
            }
            data-testid="kig-tips"
            aria-describedby="kig-tips-hint"
          />
          <span>
            {T('Send meg korte tips om oppgaver som gjenstår')}
            <small id="kig-tips-hint">
              {T(
                'Bare om det neste steget du selv kan gjøre, høyst ett om dagen, mandag til fredag. Lenken i e-posten viser hvordan det står nå og gjør ingenting av seg selv.',
              )}
            </small>
          </span>
        </label>
        {e.opt_in && (
          <p className={s.meta} aria-live="polite" data-testid="kig-tips-state">
            {!e.sending
              ? T('Utsending av tips er ikke slått på ennå. Valget ditt er lagret.')
              : ((why && T(why)) ?? T('Neste tips kan komme i neste utsendelse.'))}
            {e.last_sent_at
              ? ` ${T('Siste tips: {date}.', { date: fmtD(e.last_sent_at.slice(0, 10)) })}`
              : ''}
          </p>
        )}
      </div>
    </section>
  );
}

const DRAFT_COPY: Record<SetupDraft['status'], string> = {
  none: 'Ikke påbegynt.',
  draft: 'Utkast lagret {date}. Ikke koblet til ennå.',
  paused: 'Satt på pause. Valgene er beholdt, og du får ingen påminnelse.',
  done: 'Koblet til. Utkastet er beholdt som historikk.',
  off: 'Den direkte koblingen er ikke slått på ennå. Valgene er beholdt.',
};

/** Started optional setups, resumed from here (#297 ONB-23/24); one nobody started is listed only in Innstillinger. */
function OptionalSetup() {
  const q = useSetupDrafts();
  // the setup's own import draft is resumed from the setup entry above, not listed here (#154)
  const list = (q.data ?? []).filter((d) => d.setup === 'lonnskobling' && d.status !== 'none');
  if (q.isError)
    return (
      <p className={s.meta} aria-live="polite">
        {T('Valgfritt oppsett kunne ikke leses.')}{' '}
        <button type="button" className={s.textBtn} onClick={() => q.refetch()}>
          {T('Prøv igjen')}
        </button>
      </p>
    );
  if (!list.length) return null;
  return (
    <section className={s.section} aria-labelledby="kig-valgfritt" data-testid="kig-optional">
      <h2 id="kig-valgfritt">{T('Valgfritt oppsett')}</h2>
      <ul>
        {list.map((d) => (
          <li key={d.setup} className={s.row} data-testid={`draft-${d.setup}`} data-state={d.status}>
            <span className={`${s.dot} ${d.status === 'done' ? s.dotDone : ''}`} aria-hidden="true">
              {d.status === 'done' && <Icon name="check" size={14} strokeWidth={2.4} />}
            </span>
            <span className={s.rText}>
              <b>
                {T('Tilkobling til {system}', {
                  system: d.system === 'poweroffice' ? 'PowerOffice Go' : 'Tripletex',
                })}
              </b>
              <span>
                {T(DRAFT_COPY[d.status], { date: d.saved_at ? fmtD(d.saved_at.slice(0, 10)) : '' })}
                {d.status === 'draft' && d.remind_on
                  ? ` ${T('Påminnelse {date}.', { date: fmtD(d.remind_on) })}`
                  : ''}
              </span>
            </span>
            <Link
              className={s.go}
              to="/innstillinger/lonn#koblingsutkast"
              aria-label={`${d.status === 'done' ? T('Se') : T('Fortsett')}: ${T('Tilkobling til {system}', {
                system: d.system === 'poweroffice' ? 'PowerOffice Go' : 'Tripletex',
              })}`}
            >
              {d.status === 'done' ? T('Se') : T('Fortsett')}
              <Icon name="chevronRight" size={16} />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * The way back into the first-run setup by what is actually saved (#154 HUG-01, HUG-07, OP-03): an import
 * that was started and not reviewed says «Fortsett importen», with the date it was put off to; a reviewed
 * import with the setup still open says «Fullfør med AI»; a company with nobody added yet and no import
 * started is offered «Velg system». A deferral is shown as not done. The administrator's only.
 */
function SetupEntry({ o }: { o: Onboarding }) {
  const { tenant } = useTenant();
  const tw = useTenantWrite();
  const nav = useNavigate();
  if (!tenant?.me?.is_admin || tenant.is_demo) return null;
  const imp = o.import ?? null;
  const step = tenant.setup_step ?? null;
  let title: string;
  let line: string;
  let action: string;
  let to: 'settings' | 'company' | 'hugin';
  if (imp && imp.status !== 'done') {
    title = T('Importen er ikke ferdig');
    line = imp.remind_on
      ? T('Satt til {date}. Det som er hentet, er lagret; resten venter.', { date: fmtD(imp.remind_on) })
      : T('Se over det som ble funnet, og velg hva som skal importeres.');
    action = T('Fortsett importen');
    to = 'company';
  } else if (imp && step) {
    title = T('Fullfør oppsettet med AI');
    line = T('Hugin hjelper med det som mangler, som åpningstider, vakttyper og bemanning.');
    action = T('Fullfør med AI');
    to = 'hugin';
  } else if (!imp && o.facts.people === 0) {
    title = T('Begynn med systemet dere bruker');
    line = T('Hent det som finnes først. Hugin hjelper med det som mangler etterpå.');
    action = T('Velg system');
    to = 'settings';
  } else return null;
  return (
    <div className={s.entry} data-testid="kig-setup-entry">
      <Icon name="sparkle" size={18} />
      <span>
        <b>{title}</b>
        <span>{line}</span>
      </span>
      <Button
        tone="pri"
        small
        disabled={tw.isPending}
        onClick={() =>
          (step === to
            ? Promise.resolve()
            : tw.mutateAsync({ id: tenant.id, body: { setup_step: to } })
          ).then(
            () => nav('/oppsett'),
            (e) => toast(errorText(e)),
          )
        }
      >
        {action}
      </Button>
    </div>
  );
}
